/**
 * Utility functions for exporting data to CSV and XLSX
 */

export function exportToCSV<T extends Record<string, any>>(
    data: T[],
    filename: string,
    columns: { key: keyof T | string; title: string }[]
): void {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    // Build CSV header
    const headers = columns.map((col) => `"${col.title}"`).join(',');

    // Build CSV rows
    const rows = data.map((item) => {
        return columns
            .map((col) => {
                const keyStr = String(col.key);
                let value = keyStr.includes('.')
                    ? keyStr.split('.').reduce((obj: any, key: string) => obj?.[key], item)
                    : item[col.key as keyof T];

                // Handle null/undefined
                if (value === null || value === undefined) {
                    value = '';
                }

                // Convert to string and escape quotes
                const strValue = String(value).replace(/"/g, '""');
                return `"${strValue}"`;
            })
            .join(',');
    });

    const csvContent = [headers, ...rows].join('\n');

    // Create blob and trigger download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Export data to XLSX format using SheetJS
 * Note: Requires 'xlsx' package to be installed: npm install xlsx
 */
export async function exportToXLSX<T extends Record<string, any>>(
    data: T[],
    filename: string,
    columns: { key: keyof T | string; title: string }[]
): Promise<void> {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    // Dynamically import xlsx to avoid bundling if not used
    const XLSX = await import('xlsx');

    // Prepare data for XLSX
    const headers = columns.map((col) => col.title);
    const rows = data.map((item) => {
        return columns.map((col) => {
            const keyStr = String(col.key);
            let value = keyStr.includes('.')
                ? keyStr.split('.').reduce((obj: any, key: string) => obj?.[key], item)
                : item[col.key as keyof T];

            // Handle null/undefined
            if (value === null || value === undefined) {
                return '';
            }
            return value;
        });
    });

    // Create worksheet
    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-adjust column widths
    const colWidths = columns.map((col, idx) => {
        const headerLen = col.title.length;
        const maxDataLen = Math.max(...rows.map(row => String(row[idx] || '').length));
        return { wch: Math.min(Math.max(headerLen, maxDataLen) + 2, 50) };
    });
    ws['!cols'] = colWidths;

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dane');

    // Generate and download file
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

/**
 * Unified export function with format selection
 */
export async function exportData<T extends Record<string, any>>(
    data: T[],
    filename: string,
    columns: { key: keyof T | string; title: string }[],
    format: 'CSV' | 'XLSX' = 'CSV'
): Promise<void> {
    if (format === 'XLSX') {
        await exportToXLSX(data, filename, columns);
    } else {
        exportToCSV(data, filename, columns);
    }
}
